<template>
  <div class="rv-dns" data-test="reviews-do-not-send">
    <div class="rv-section-head">
      <div>
        <h2 class="rv-h2">Do not send</h2>
        <div class="rv-h2-sub">
          Patients on this list never get a review request, automatic or manual. Patients who reply STOP
          to a text appear here on their own.
        </div>
      </div>
      <button type="button" class="rv-btn rv-btn-primary" data-test="dns-add" @click="openAdd">
        <i class="fa fa-plus" aria-hidden="true"></i> Add patient
      </button>
    </div>

    <div class="rv-toolbar">
      <!-- Filter toggles (aria-pressed), not role="tab": there is no tabpanel / arrow-key model. -->
      <div class="rv-pills" role="group" aria-label="Filter the list">
        <button
          v-for="s in sources"
          :key="s.value"
          type="button"
          class="rv-pill"
          :aria-pressed="source === s.value ? 'true' : 'false'"
          :class="{ active: source === s.value }"
          :data-test="'dns-filter-' + s.value"
          @click="setSource(s.value)"
        >
          {{ s.label }} <span class="rv-pill-count">{{ countFor(s.value) }}</span>
        </button>
      </div>
      <div class="rv-search">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          type="text"
          class="rv-search-input"
          data-test="dns-search"
          placeholder="Search by patient name"
          :value="search"
          @input="onSearchInput($event.target.value)"
        />
      </div>
    </div>

    <div class="rv-card rv-table-card">
      <div class="rv-table-head">
        <span>Patient</span>
        <span>Reason</span>
        <span>Note</span>
        <span>Added by</span>
        <span>Added</span>
        <span>Until</span>
        <span class="rv-right">Actions</span>
      </div>

      <div v-if="loading && !rows.length" class="rv-table-empty">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
      </div>

      <div v-else-if="!rows.length" class="rv-table-empty" data-test="dns-empty">
        <i class="fa fa-inbox"></i>
        <span>{{ emptyText }}</span>
      </div>

      <div
        v-else
        v-for="row in rows"
        :key="rowKey(row)"
        class="rv-row"
        :class="{ 'is-stop': row.source === 'stop' }"
        data-test="dns-row"
      >
        <div class="rv-cell rv-patient">
          <div class="rv-avatar">{{ initials(row.patientName) }}</div>
          <div class="rv-patient-meta">
            <div class="rv-name">{{ row.patientName || 'Unknown patient' }}</div>
            <div class="rv-pid">#{{ row.patNum }}</div>
          </div>
        </div>

        <div class="rv-cell">
          <span v-if="row.source === 'stop'" class="rv-chip rv-chip-gray" data-test="dns-stop-chip">Patient replied STOP</span>
          <span v-else class="rv-chip" :class="{ 'rv-chip-muted': !row.reason }">{{ reasonLabel(row.reason) }}</span>
        </div>

        <div class="rv-cell rv-note" :title="row.note || ''">{{ row.note || '—' }}</div>

        <div class="rv-cell rv-muted-cell">{{ row.source === 'stop' ? 'Patient' : (row.addedByName || '—') }}</div>

        <div class="rv-cell rv-muted-cell">{{ formatAdded(row.createdAt) }}</div>

        <div class="rv-cell rv-muted-cell">{{ row.source === 'stop' ? '—' : expiry(row.expiresAt) }}</div>

        <div class="rv-cell rv-right rv-actions">
          <template v-if="row.source !== 'stop'">
            <button
              type="button"
              class="rv-row-btn"
              data-test="dns-edit"
              :disabled="isRemoving(row)"
              :aria-label="'Edit Do not send entry for ' + (row.patientName || 'this patient')"
              @click="openEdit(row)"
            >Edit</button>
            <button
              type="button"
              class="rv-row-btn rv-row-btn-danger"
              data-test="dns-remove"
              :disabled="isRemoving(row)"
              :aria-label="'Remove ' + (row.patientName || 'this patient') + ' from the Do not send list'"
              @click="remove(row)"
            >
              {{ isRemoving(row) ? 'Removing…' : 'Remove' }}
            </button>
          </template>
          <span v-else class="rv-no-action">—</span>
        </div>
      </div>
    </div>

    <div class="rv-pager" v-if="total > pageSize">
      <button class="rv-pager-btn" :disabled="offset === 0 || loading" @click="prevPage">
        <i class="fa fa-chevron-left"></i> Prev
      </button>
      <span class="rv-pager-info">{{ rangeStart }}–{{ rangeEnd }} of {{ total }}</span>
      <button class="rv-pager-btn" :disabled="offset + pageSize >= total || loading" @click="nextPage">
        Next <i class="fa fa-chevron-right"></i>
      </button>
    </div>

    <DoNotSendModal
      :show="modal.open"
      :mode="modal.mode"
      :patient="modal.patient"
      :initial="modal.initial"
      @saved="onSaved"
      @close="modal.open = false"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';
import DoNotSendModal from './DoNotSendModal.vue';
import {
  DO_NOT_SEND_SOURCES, reasonLabel, expiryLabel, errorForLog, doNotSendRowKey, pageOffsetAfterEmpty,
} from '../doNotSend';

const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'ReviewsDoNotSend',
  components: { DoNotSendModal },
  mixins: [mixin],

  data() {
    return {
      source: 'all',
      search: '',
      offset: 0,
      pageSize: 50,
      // doNotSendRowKey(row) values — the same composite identity as the row :key.
      removingKeys: [],
      sources: DO_NOT_SEND_SOURCES,
      searchTimer: null,
      modal: { open: false, mode: 'add', patient: null, initial: null },
    };
  },

  created() {
    // Not reactive on purpose: only the latest load() may act on its response.
    this.loadSeq = 0;
    this.load();
  },

  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },

  computed: {
    ...mapState('reviews', {
      list: (s) => s.doNotSend,
      loading: (s) => s.loading.doNotSend,
    }),

    rows() { return (this.list && Array.isArray(this.list.rows)) ? this.list.rows : []; },
    total() { return (this.list && this.list.total) || 0; },
    counts() { return (this.list && this.list.counts) || { staff: 0, stop: 0 }; },

    timezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || 'America/New_York';
    },

    emptyText() {
      if (this.search.trim()) return 'No patients on the list match that name.';
      if (this.source === 'stop') return 'No patients have replied STOP.';
      if (this.source === 'staff') return 'No patients have been added by staff.';
      return 'No one is on the Do not send list.';
    },

    rangeStart() { return this.total === 0 ? 0 : this.offset + 1; },
    rangeEnd() { return Math.min(this.offset + this.pageSize, this.total); },
  },

  methods: {
    ...mapActions('reviews', ['getDoNotSendList', 'setReviewPatientOptout', 'getReviewsDashboard']),

    async load() {
      if (!this.siteID) return;
      const seq = ++this.loadSeq;
      try {
        const payload = { siteID: this.siteID, source: this.source, limit: this.pageSize, offset: this.offset };
        const q = this.search.trim();
        if (q) payload.search = q;
        const data = await this.getDoNotSendList(payload);
        if (seq !== this.loadSeq) return;
        // Removing (or editing away) the last row of a later page leaves that page EMPTY — the
        // pager can hide and the empty state lies. Step back to the last page that has rows.
        const rows = data && Array.isArray(data.rows) ? data.rows : [];
        if (!rows.length) {
          const back = pageOffsetAfterEmpty(this.offset, data && data.total, this.pageSize);
          if (back !== null && back !== this.offset) {
            this.offset = back;
            await this.load();
          }
        }
      } catch (err) {
        console.error('[ReviewsDoNotSend] load failed:', errorForLog(err));
        if (seq !== this.loadSeq) return;
        this.$toast && this.$toast.error('Could not load the Do not send list.');
      }
    },

    countFor(value) {
      const c = this.counts;
      if (value === 'staff') return c.staff || 0;
      if (value === 'stop') return c.stop || 0;
      return (c.staff || 0) + (c.stop || 0);
    },

    setSource(value) {
      if (value === this.source) return;
      this.source = value;
      this.offset = 0;
      this.load();
    },

    onSearchInput(value) {
      this.search = value;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.offset = 0;
        this.load();
      }, SEARCH_DEBOUNCE_MS);
    },

    nextPage() {
      if (this.offset + this.pageSize < this.total) {
        this.offset += this.pageSize;
        this.load();
      }
    },

    prevPage() {
      if (this.offset > 0) {
        this.offset = Math.max(0, this.offset - this.pageSize);
        this.load();
      }
    },

    openAdd() {
      this.modal = { open: true, mode: 'add', patient: null, initial: null };
    },

    openEdit(row) {
      this.modal = {
        open: true,
        mode: 'edit',
        patient: { patNum: row.patNum, patientName: row.patientName },
        initial: { reason: row.reason, note: row.note, expiresAt: row.expiresAt },
      };
    },

    async onSaved(entry, meta) {
      const who = (meta && meta.patientName) || 'This patient';
      this.$toast && this.$toast.success(
        this.modal.mode === 'edit' ? 'Entry updated.' : `${who} was added to the Do not send list.`
      );
      await this.load();
      this.refreshDashboard();
    },

    async remove(row) {
      if (this.isRemoving(row)) return;
      const who = row.patientName || 'this patient';
      if (!window.confirm(`Remove ${who} from the Do not send list?\n\nThey can be asked for a review again after future visits.`)) return;
      const key = this.rowKey(row);
      this.removingKeys.push(key);
      try {
        await this.setReviewPatientOptout({
          siteID: this.siteID,
          patNum: row.patNum,
          optedOut: false,
          employeeID: this.user && this.user.EmployeeID,
        });
        this.$toast && this.$toast.success(`${row.patientName || 'Patient'} was removed from the list.`);
        await this.load();
        this.refreshDashboard();
      } catch (err) {
        console.error('[ReviewsDoNotSend] remove failed:', errorForLog(err));
        this.$toast && this.$toast.error((err && err.code && err.message) || 'Could not remove this patient.');
      } finally {
        this.removingKeys = this.removingKeys.filter((k) => k !== key);
      }
    },

    // The Requests tab's "Do not send" stat card reads the dashboard — keep it honest.
    refreshDashboard() {
      this.getReviewsDashboard({ siteID: this.siteID, rangeDays: 30 }).catch(() => {});
    },

    isRemoving(row) {
      return this.removingKeys.includes(this.rowKey(row));
    },

    // One identity for the row :key and the busy state — patNum alone repeats across clinic SiteIDs.
    rowKey(row) {
      return doNotSendRowKey(row);
    },

    reasonLabel(code) {
      return reasonLabel(code);
    },

    // expiresAt and the opt-out createdAt are ISO-8601 UTC instants → CONVERT into the site zone.
    formatUtcDate(value) {
      if (!value) return '';
      const m = moment.utc(value).tz(this.timezone);
      return m.isValid() ? m.format('MMM D, YYYY') : '';
    },

    expiry(expiresAt) {
      return expiryLabel(expiresAt, (v) => this.formatUtcDate(v));
    },

    formatAdded(value) {
      return this.formatUtcDate(value) || '—';
    },

    initials(name) {
      if (!name) return '?';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$border: #e2e8f0;
$border-strong: #cbd5e1;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.rv-dns { display: flex; flex-direction: column; gap: 16px; }

.rv-section-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.rv-h2 { font-size: 16px; font-weight: 800; margin: 0; color: $neutral-900; }
.rv-h2-sub { font-size: 12.5px; color: $neutral-500; margin-top: 2px; max-width: 620px; }

.rv-btn {
  border: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600;
  padding: 8px 16px; border-radius: 9px; white-space: nowrap; transition: background 0.15s ease;
  .fa { margin-right: 4px; }
}
.rv-btn-primary { background: $primary; color: #fff; &:hover { background: #0b5f58; } }

.rv-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.rv-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.rv-pill {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid $border; background: #fff; border-radius: 999px; cursor: pointer; font-family: inherit;
  padding: 6px 12px; font-size: 12.5px; font-weight: 600; color: #334155;
  &:hover { border-color: #9fd7cd; }
  &.active { border-color: $primary; background: #edfbf6; color: $primary; }
}
.rv-pill-count {
  min-width: 20px; padding: 0 6px; border-radius: 999px; background: #f1f5f9;
  font-size: 11px; font-weight: 700; color: $neutral-500; text-align: center; font-variant-numeric: tabular-nums;
}
.rv-pill.active .rv-pill-count { background: #fff; color: $primary; }

.rv-search { position: relative; width: 260px; max-width: 100%; }
.rv-search .fa { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: $neutral-400; font-size: 13px; pointer-events: none; }
.rv-search-input {
  width: 100%; box-sizing: border-box; height: 36px;
  border: 1px solid $border-strong; border-radius: 9px; padding: 7px 11px 7px 32px;
  font-family: inherit; font-size: 13px; color: #334155;
}

.rv-card { background: #fff; border: 1px solid $border; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); }
.rv-table-card { overflow: hidden; }

.rv-table-head, .rv-row {
  display: grid;
  grid-template-columns: 1.4fr 1.1fr 1.6fr 1fr 0.9fr 1fr 1fr;
  gap: 14px;
  padding: 12px 18px;
  align-items: center;
}
.rv-table-head {
  border-bottom: 1px solid $border;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: $neutral-500;
}
.rv-row { border-bottom: 1px solid #f1f5f9; &:hover { background: #fafbfb; } }
.rv-right { text-align: right; justify-content: flex-end; display: flex; }
.rv-cell { min-width: 0; }
.rv-actions { align-items: center; gap: 8px; }

.rv-patient { display: flex; align-items: center; gap: 11px; }
.rv-avatar {
  width: 32px; height: 32px; border-radius: 999px; flex-shrink: 0;
  background: #e2e8f0; color: #475569; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.rv-patient-meta { min-width: 0; }
.rv-name { font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: $neutral-900; }
.rv-pid { font-size: 12px; color: $neutral-500; font-family: monospace; }

.rv-chip {
  display: inline-block; max-width: 100%;
  padding: 3px 10px; border-radius: 999px;
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  font-size: 12px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.rv-chip-muted { background: #f8fafc; border-color: $border; color: $neutral-500; font-weight: 600; }
.rv-chip-gray { background: #f1f5f9; border-color: $border-strong; color: #475569; }

.rv-note {
  font-size: 12.5px; color: #475569;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word;
}
.rv-muted-cell { font-size: 12.5px; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.rv-row-btn {
  border: 1px solid $border; background: #fff; color: $neutral-500; font-family: inherit;
  font-size: 12px; font-weight: 600; cursor: pointer;
  padding: 5px 11px; border-radius: 7px; white-space: nowrap;
  &:hover:not(:disabled) { border-color: #9fd7cd; color: $primary; background: #edfbf6; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}
.rv-row-btn-danger:hover:not(:disabled) { border-color: #fca5a5; color: #b91c1c; background: #fef2f2; }
.rv-no-action { color: $neutral-400; }

.rv-table-empty {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  padding: 44px 18px; color: $neutral-500; font-size: 13.5px;
  .fa { font-size: 18px; color: $neutral-400; }
}

.rv-pager { display: flex; align-items: center; justify-content: center; gap: 16px; }
.rv-pager-btn {
  border: 1px solid $border-strong; background: #fff; border-radius: 9px; padding: 7px 14px;
  font-size: 13px; font-weight: 600; color: #334155; cursor: pointer; font-family: inherit;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { background: #f8fafc; }
}
.rv-pager-info { font-size: 12.5px; color: $neutral-500; }

@media (max-width: 991px) {
  .rv-table-head { display: none; }
  .rv-row { grid-template-columns: 1fr 1fr; row-gap: 8px; }
  .rv-note { grid-column: 1 / -1; }
  .rv-right { grid-column: 2; }
}
@media (max-width: 575px) {
  .rv-search { width: 100%; }
}
</style>
